import { useQuery } from '@tanstack/react-query';
import { Tag } from 'lucide-react';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import type { AdminPlanCatalogItem, AdminPlanCatalogPrice } from '@pnl/shared';
import { api } from '../lib/api';

function fmtMoney(cents: number, currency: 'BRL' | 'USD'): string {
  return (cents / 100).toLocaleString(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  });
}

const recurrenceLabel: Record<AdminPlanCatalogPrice['recurrence'], string> = {
  ONE_TIME: 'único',
  MONTHLY: '/mês',
  QUARTERLY: '/trimestre',
  YEARLY: '/ano',
};

function priceLine(p: AdminPlanCatalogPrice): string {
  return `${fmtMoney(p.amountCents, p.currency)} ${recurrenceLabel[p.recurrence]}`;
}

export function PlansPage() {
  const query = useQuery({
    queryKey: ['admin', 'plan-catalog'],
    queryFn: () => api.listPlanCatalog(),
  });
  const items = query.data?.items ?? [];
  const b3 = items.filter((p) => p.marketCode === 'B3');
  const forex = items.filter((p) => p.marketCode === 'FOREX');

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Catálogo</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Planos
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Planos e preços por mercado (capital, drawdown e meta). Leitura — a edição de preço é
          feita no seed/deploy para manter os sites de venda em sincronia.
        </p>
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={<Tag size={28} />} title="Nenhum plano" description="Catálogo vazio." />
      ) : (
        <>
          <PlanTable title="B3" plans={b3} />
          <PlanTable title="Forex" plans={forex} />
          <Alert tone="info" title="Edição de preço">
            Para alterar preços, atualize o seed/configuração e faça deploy — assim o checkout dos
            sites (preços) fica consistente com o backend. Descontos pontuais saem por cupom.
          </Alert>
        </>
      )}
    </div>
  );
}

function PlanTable({ title, plans }: { title: string; plans: AdminPlanCatalogItem[] }) {
  if (plans.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        <Table>
          <THead>
            <TR>
              <TH>Plano</TH>
              <TH className="text-right">Capital</TH>
              <TH className="text-right">Drawdown</TH>
              <TH className="text-right">Meta</TH>
              <TH>Preços</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {plans.map((p) => {
              const cur: 'BRL' | 'USD' = p.marketCode === 'FOREX' ? 'USD' : 'BRL';
              return (
                <TR key={p.id}>
                  <TD>
                    <div className="text-ink-900 text-sm font-semibold">{p.name}</div>
                    <div className="text-ink-500 font-mono text-xs">{p.code}</div>
                  </TD>
                  <TD className="text-right tabular-nums">
                    {fmtMoney(p.capitalInitialCents, cur)}
                  </TD>
                  <TD className="text-right tabular-nums">{fmtMoney(p.drawdownMaxCents, cur)}</TD>
                  <TD className="text-right tabular-nums">{fmtMoney(p.metaCents, cur)}</TD>
                  <TD>
                    {p.prices.length === 0 ? (
                      <span className="text-ink-400 text-xs">—</span>
                    ) : (
                      <div className="text-ink-700 space-y-0.5 text-xs tabular-nums">
                        {p.prices.map((pr) => (
                          <div key={pr.id}>{priceLine(pr)}</div>
                        ))}
                      </div>
                    )}
                  </TD>
                  <TD>
                    <Badge tone={p.active ? 'success' : 'neutral'}>
                      {p.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </CardBody>
    </Card>
  );
}
